import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Select as MaterialSelect,
  Checkbox as MaterialCheckbox,
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  InputAdornment,
  IconButton,
  ListItemText,
} from "@material-ui/core";
import { KeyboardDatePicker, KeyboardTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import MUIAutocomplete from "@material-ui/lab/Autocomplete";
import UnCheckedIcon from "@material-ui/icons/CheckBoxOutlineBlankSharp";
import CheckedIcon from "@material-ui/icons/CheckBoxSharp";
import CalendarIcon from "@material-ui/icons/EventSharp";
import AccessTimeIcon from "@material-ui/icons/AccessTime";

const mondayWeekConfig = { week: { dow: 1, doy: 4 } };
const sundayWeekConfig = { week: { dow: 0, doy: 6 } };
moment.updateLocale("de", mondayWeekConfig);
moment.updateLocale("en", sundayWeekConfig);

const getMomentAdapterLocale = (i18nLanguage) => {
  const raw = (i18nLanguage || "en").toLowerCase();
  if (raw.startsWith("de")) return "de";
  if (raw.startsWith("en")) return "en";
  return "en";
};

const getErrorForName = (errors, name) => {
  if (!errors) {
    return null;
  }

  if (errors[name]) {
    return errors[name];
  }

  if (name.includes("[") && name.includes("]")) {
    const parts = name.split(/[\[\].]/).filter(part => part !== "");
    let tmp = errors;

    for (let i = 0; i < parts.length; i++) {
      if (tmp && typeof tmp === 'object' && tmp[parts[i]] !== undefined) {
        tmp = tmp[parts[i]];
      } else {
        tmp = null;
        break;
      }
    }

    if (tmp) {
      return tmp;
    }
  }

  if (name.includes(".")) {
    const split = name.split(".");

    if (errors[split[0]]) {
      let tmp = errors[split[0]];
      for (let i = 1; i < split.length; i += 1) {
        if (tmp[split[i]]) {
          tmp = tmp[split[i]];
        } else {
          tmp = null;
          break;
        }
      }

      if (tmp) {
        return tmp;
      }
    }
  }

  return null;
};

const getHelpOrError = (help, errors, name, t) => {
  const error = getErrorForName(errors, name);

  if (error) {
    return t(error);
  }

  if (help) {
    return help;
  }

  return null;
};

export const Password = (props) => {
  const [showPwd, setShowPwd] = React.useState(false);
  const { type, label, name, errors, value, help, ...rest } = props;
  const { t } = useTranslation();

  const changeShowPwd = () => {
    setShowPwd(!showPwd);
  };

  return (
    <TextField
      label={label || ""}
      margin="normal"
      name={name}
      type={showPwd ? "text" : "password"}
      value={value || ""}
      fullWidth={true}
      helperText={getHelpOrError(help, errors, name, t)}
      error={!!getErrorForName(errors, name)}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={changeShowPwd}
            >
              {showPwd ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </IconButton>
          </InputAdornment>
        ),
      }}
      variant="filled"
      {...rest}
    />
  );
};

export const Input = (props) => {
  const { type, label, name, errors, value, help, ...rest } = props;
  const { t } = useTranslation();

  return (
    <TextField
      label={label || ""}
      margin="dense"
      name={name}
      type={type || "text"}
      value={value || ""}
      fullWidth={true}
      helperText={getHelpOrError(help, errors, name, t)}
      error={!!getErrorForName(errors, name)}
      inputProps={{ autoComplete: "off", ...rest.inputProps }}
      variant="filled"
      {...rest}
    />
  );
};

export const InputOnlyNumbers = (props) => {
  const { type, label, name, errors, value, help, onChange, ...rest } = props;
  const { t } = useTranslation();

  return (
    <TextField
      label={label || ""}
      margin="dense"
      name={name}
      type={type || "text"}
      value={value || ""}
      fullWidth={true}
      helperText={getHelpOrError(help, errors, name, t)}
      error={!!getErrorForName(errors, name)}
      inputProps={{ autoComplete: "off" }}
      variant="filled"
      onChange={(e) => {
        if (!/^[0-9+-\s]*$/.test(e.target.value)) {
          return;
        }

        return onChange(e);
      }}
      {...rest}
    />
  );
};

export const Select = (props) => {
  const {
    type,
    label,
    name,
    errors,
    value,
    options,
    help,
    margin,
    InputLabelProps,
    ...rest
  } = props;
  const { t } = useTranslation();

  return (
    <FormControl fullWidth margin="none" style={{ marginTop: "8px" }}>
      {!!label && (
        <InputLabel variant={"filled"} margin={"dense"} {...InputLabelProps}>
          {label || ""}
        </InputLabel>
      )}
      <MaterialSelect
        margin="dense"
        name={name}
        value={value || ""}
        fullWidth={true}
        error={!!getErrorForName(errors, name)}
        variant="filled"
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          getContentAnchorEl: null,
        }}
        {...rest}
      >
        {options.map((o, i) => (
          <MenuItem key={i} value={o.value} disabled={!!o.disabled}>
            {o.label}
          </MenuItem>
        ))}
      </MaterialSelect>
      <FormHelperText error={!!getErrorForName(errors, name)}>
        <span>{getHelpOrError(help, errors, name, t)}</span>
      </FormHelperText>
    </FormControl>
  );
};

export const Textarea = (props) => {
  const { label, name, value, help, errors, ...rest } = props;
  const { t } = useTranslation();

  return (
    <TextField
      label={label || ""}
      margin="normal"
      name={name}
      type={"text"}
      value={value || ""}
      fullWidth={true}
      helperText={getHelpOrError(help, errors, name, t)}
      error={!!getErrorForName(errors, name)}
      {...rest}
      multiline={true}
      rows={3}
      variant="filled"
      inputProps={{ autoComplete: "off" }}
    />
  );
};

export const Checkbox = (props) => {
  const { label, name, value, ...rest } = props;

  return (
    <MaterialCheckbox
      checked={value}
      value={value}
      name={name}
      icon={<UnCheckedIcon />}
      checkedIcon={<CheckedIcon />}
      {...rest}
    />
  );
};

export const Autocomplete = ({
  name,
  value,
  label,
  help,
  errors,
  options,
  required,
  onChange,
  settings,
  onBlur,
  disabled,
}) => {
  const { t } = useTranslation();
  const opts = {
    disableClearable: false,
    freeSolo: false,
    valueProp: "value",
    labelProp: "label",
    filterOptions: null,
    disableInputWhenNoOptions: true,
    ...settings,
  };

  const getValue = (o) => {
    if (typeof opts.valueProp === "function") {
      return opts.valueProp(o);
    }

    return o[opts.valueProp];
  };

  const getLabel = (o) => {
    if (typeof opts.labelProp === "function") {
      return opts.labelProp(o);
    }

    return o[opts.labelProp];
  };

  // this prevents a bug when we have an input with
  // options fetched from the api. The input loads before the api
  // and later on the currently selected item is not properly marked.
  if (!options.length && opts.disableInputWhenNoOptions) {
    return (
      <Select
        name={name}
        value={value}
        label={label}
        help={help}
        errors={errors}
        options={[{ label: t("No options"), value: null, disabled: true }]}
        required={required}
        onChange={null}
        onBlur={null}
        disabled={disabled}
        variant="filled"
      />
    );
  }

  return (
    <FormControl fullWidth margin="none">
      <MUIAutocomplete
        id="new-select-autocomplete"
        fullWidth
        value={value ? options.find((o) => getValue(o) === value) : null}
        options={options}
        disableClearable={opts.disableClearable}
        onChange={(event, newValue) => {
          onChange(newValue);
        }}
        onBlur={onBlur}
        disabled={disabled}
        freeSolo={opts.freeSolo}
        getOptionLabel={(option) => getLabel(option)}
        noOptionsText={t("No options")}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t(label)}
            required={required}
            inputProps={{
              ...params.inputProps,
              autoComplete: `off`,
            }}
            margin="dense"
            error={!!getHelpOrError(null, errors, name, t)}
            helperText={getHelpOrError(help, errors, name, t)}
            variant="filled"
          />
        )}
        {...(opts.filterOptions ? { filterOptions: opts.filterOptions } : {})}
      />
    </FormControl>
  );
};

export const DatePicker = (props) => {
  const {
    label,
    name,
    errors,
    value,
    help,
    onChange,
    required,
    disablePast,
    clearable,
    inputVariant,
    InputProps,
    inputProps,
    className,
    readOnlyInput = false,
    readOnly = false,
    disabled,
    ...rest
  } = props;
  const { t, i18n } = useTranslation("common");
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();

  const isGerman = currentLanguage.startsWith("de");
  const dateFormat = "DD.MM.YYYY";
  const placeholder = isGerman ? "TT.MM.JJJJ" : "DD.MM.YYYY";
  const adapterLocale = getMomentAdapterLocale(currentLanguage);

  moment.locale(adapterLocale);

  const error = !!getHelpOrError(null, errors, name, t);
  const helperText = getHelpOrError(help, errors, name, t);
  const variant = inputVariant || "filled";
  const [open, setOpen] = React.useState(false);

  const isNonWritable = readOnlyInput || readOnly;

  const handleDateChange = (date, keyboardInputValue) => {
    if (disablePast && date && moment.isMoment(date) && date.isValid()) {
      if (date.isBefore(moment(), "day")) {
        return;
      }
    }
    if (onChange) {
      onChange(date, keyboardInputValue);
    }
  };

  return (
    <MuiPickersUtilsProvider
      utils={MomentUtils}
      locale={adapterLocale}
      libInstance={moment}
      key={adapterLocale}
    >
      <KeyboardDatePicker
        name={name}
        value={value == null ? null : value}
        placeholder={placeholder}
        onChange={handleDateChange}
        disablePast={disablePast}
        minDate={disablePast ? moment().startOf("day") : rest.minDate}
        clearable={clearable}
        cancelLabel={isGerman ? "ABBRECHEN" : "CANCEL"}
        okLabel="OK"
        clearLabel={isGerman ? "LÖSCHEN" : "CLEAR"}
        keyboardIcon={<CalendarIcon />}
        format={dateFormat}
        label={t(label)}
        margin="dense"
        autoOk
        className={className}
        disabled={disabled}
        open={open}
        onOpen={() => {
          if (!disabled && !readOnly) setOpen(true);
        }}
        onClose={() => setOpen(false)}
        onClick={() => {
          if (!disabled && !readOnly && isNonWritable) {
            setOpen(true);
          }
        }}
        inputProps={{
          autoComplete: "off",
          ...(isNonWritable ? { readOnly: true } : {}),
          ...inputProps,
          style: {
            ...(isNonWritable && !disabled && !readOnly ? { cursor: "pointer" } : {}),
            ...(inputProps?.style || {}),
          },
        }}
        InputProps={{
          ...InputProps,
          style: {
            ...(isNonWritable && !disabled && !readOnly ? { cursor: "pointer" } : {}),
            ...(InputProps?.style || {}),
          },
        }}
        inputVariant={variant}
        required={required}
        fullWidth
        error={error}
        helperText={helperText}
        {...rest}
      />
    </MuiPickersUtilsProvider>
  );
};

export const TimePicker = (props) => {
  const {
    label,
    name,
    errors,
    value,
    help,
    onChange,
    required,
    clearable,
    inputVariant,
    InputProps,
    inputProps,
    className,
    readOnlyInput = false,
    readOnly = false,
    disabled,
    ampm = false,
    format = "HH:mm",
    ...rest
  } = props;
  const { t, i18n } = useTranslation("common");
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
  const isGerman = currentLanguage.startsWith("de");
  const adapterLocale = getMomentAdapterLocale(currentLanguage);

  const error = !!getHelpOrError(null, errors, name, t);
  const helperText = getHelpOrError(help, errors, name, t);
  const variant = inputVariant || "filled";
  const [open, setOpen] = React.useState(false);
  const isNonWritable = readOnlyInput || readOnly;

  const momentValue = React.useMemo(() => {
    if (!value) return null;
    if (moment.isMoment(value)) return value;
    if (value instanceof Date) return moment(value);
    if (typeof value === "string") {
      const parsed = moment(value, ["HH:mm:ss", "HH:mm", "hh:mm A", "hh:mm a"]);
      return parsed.isValid() ? parsed : null;
    }
    return null;
  }, [value]);

  const handleTimeChange = (date, keyboardInputValue) => {
    if (onChange) {
      if (date && moment.isMoment(date) && date.isValid()) {
        onChange(date.format("HH:mm"), date, keyboardInputValue);
      } else if (!date) {
        onChange("", null, keyboardInputValue);
      }
    }
  };

  return (
    <MuiPickersUtilsProvider
      utils={MomentUtils}
      locale={adapterLocale}
      libInstance={moment}
      key={adapterLocale}
    >
      <KeyboardTimePicker
        name={name}
        value={momentValue}
        onChange={handleTimeChange}
        clearable={clearable}
        cancelLabel={isGerman ? "ABBRECHEN" : "CANCEL"}
        okLabel="OK"
        clearLabel={isGerman ? "LÖSCHEN" : "CLEAR"}
        keyboardIcon={<AccessTimeIcon />}
        format={format}
        ampm={ampm}
        label={label ? t(label) : undefined}
        margin="dense"
        autoOk
        className={className}
        disabled={disabled}
        open={open}
        onOpen={() => {
          if (!disabled && !readOnly) setOpen(true);
        }}
        onClose={() => setOpen(false)}
        onClick={() => {
          if (!disabled && !readOnly && isNonWritable) {
            setOpen(true);
          }
        }}
        inputProps={{
          autoComplete: "off",
          ...(isNonWritable ? { readOnly: true } : {}),
          ...inputProps,
          style: {
            ...(isNonWritable && !disabled && !readOnly ? { cursor: "pointer" } : {}),
            ...(inputProps?.style || {}),
          },
        }}
        InputProps={{
          ...InputProps,
          style: {
            ...(isNonWritable && !disabled && !readOnly ? { cursor: "pointer" } : {}),
            ...(InputProps?.style || {}),
          },
        }}
        inputVariant={variant}
        required={required}
        fullWidth
        error={error}
        helperText={helperText}
        {...rest}
      />
    </MuiPickersUtilsProvider>
  );
};

export const CustomMultiSelect = ({
  values,
  name,
  label,
  errors,
  setValues,
  labelId,
  backgroundColor,
  borderColor,
  allSelected = false,
  onChange,
  onClose,
  required,
  disabled,
  labelColor = "#626262",
  displayField = "name",
}) => {
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = React.useState(setValues || []);
  const initialValuesRef = React.useRef(null);
  const isOpenRef = React.useRef(false);

  React.useEffect(() => {
    if (!isOpenRef.current) {
      setSelectedValues(setValues || []);
    }
  }, [setValues]);

  React.useEffect(() => {
    if (allSelected && !setValues?.length) {
      setSelectedValues(
        values.map((value) => (typeof value === "object" ? value.id : value))
      );
      onChange({
        target: {
          value: values.map((value) =>
            typeof value === "object" ? value.id : value
          ),
        },
      });
    }
  }, []);

  const handleChange = (event) => {
    const newValue = event.target.value;
    setSelectedValues(newValue);
    if (onChange) {
      onChange(event);
    }
  };

  const handleOpen = () => {
    isOpenRef.current = true;
    initialValuesRef.current = [...(selectedValues || [])];
  };

  const handleClose = (event) => {
    isOpenRef.current = false;
    if (onClose && initialValuesRef.current !== null) {
      const hasChanged = JSON.stringify([...selectedValues].sort()) !== JSON.stringify([...initialValuesRef.current].sort());
      if (hasChanged) {
        onClose(event, selectedValues, initialValuesRef.current);
      }
    }
  };

  return (
    <FormControl
      error={Boolean(errors?.[name])}
      fullWidth
      margin="none"
      style={{ marginTop: "8px", backgroundColor: "transparent" }}
    >
      <InputLabel
        variant="filled"
        margin="dense"
        required={required}
        id={labelId}
        style={{
          zIndex: 1000,
          backgroundColor: "transparent",
          color: labelColor,
          "&.Mui-focused": {
            color: labelColor,
          },
          "&.Mui-error": {
            color: "red",
          },
        }}
        className={labelColor === "white" ? "datepicker-label-white" : ""}
      >
        {t(label)}
      </InputLabel>
      <MaterialSelect
        labelId={labelId}
        margin="dense"
        variant="filled"
        multiple
        value={selectedValues}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        style={{
          backgroundColor: backgroundColor,
          border: "none",
          borderRadius: "0",
        }}
        className={labelColor === "white" ? "datepicker-label-white" : ""}
        renderValue={(selected) =>
          selected
            .map((value) => {
              // Check if value is an object and use the display field if it is
              const item = values.find((item) => item.id === value);
              return item ? t(item[displayField]) : t(value);
            })
            .join(", ")
        }
        required={required}
        disabled={disabled}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          getContentAnchorEl: null,
          style: { zIndex: 1500 },
          PaperProps: {
            style: {
              position: "absolute",
              marginTop: "16px",
              borderRadius: 0,
              backgroundColor: labelColor === "white" ? "#333" : undefined,
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "0 !important",
            "& fieldset": {
              border: "none !important",
              borderBottom: `1px solid ${borderColor} !important`,
            },
            "&.Mui-focused fieldset": {
              border: "none !important",
              borderBottom: `1px solid ${borderColor} !important`,
            },
          },
          "& .MuiSelect-select": {
            borderRadius: 0,
            "&:focus": {
              borderRadius: 0,
              backgroundColor: backgroundColor,
            },
          },
          "& .MuiSelect-icon": {
            color: `${borderColor}`,
          },
        }}
      >
        {values.map((value) => (
          <MenuItem
            key={typeof value === "object" ? value.id : value}
            value={typeof value === "object" ? value.id : value}
            style={{
              backgroundColor: labelColor === "white" ? "#333" : undefined,
            }}
          >
            <Checkbox
              checked={selectedValues.includes(
                typeof value === "object" ? value.id : value
              )}
              style={{
                color: labelColor === "white" ? "white" : undefined,
              }}
              sx={{
                color: labelColor === "white" ? "white !important" : undefined,
                "&.Mui-checked": {
                  color: labelColor === "white" ? "white !important" : undefined,
                },
              }}
            />
            <ListItemText
              primary={t(
                typeof value === "object" ? value[displayField] : value
              )}
              style={{
                color: labelColor === "white" ? "white" : undefined,
              }}
            />
          </MenuItem>
        ))}
      </MaterialSelect>
      {errors?.[name] && (
        <FormHelperText style={{ color: "red" }}>
          {t(errors[name])}
        </FormHelperText>
      )}
    </FormControl>
  );
};
